import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { useDropzone } from 'react-dropzone';

import api from '@/services/api';

import { dataActions } from '@/store/actions/data.actions';

import AddLetterDatePicker from '@/views/pages/AddLetter/elements/AddLetterDatePicker';
import AddLetterDeptAutoSuggest from '@/views/pages/AddLetter/elements/AddLetterDeptAutoSuggest';

import Header from '@/views/components/Header/Header';
import Footer from '@/views/components/Footer/Footer';

import { DropdownIndicator, NoOptionsMessage } from '@/views/components/CustomSelect/CustomSelect';

import Spinner from '@/views/components/Spinner/Spinner';
import AddLetterSensitiveDataWarningModal from '@/views/pages/AddLetter/elements/AddLetterSensitiveDataWarningModal';

import { useAcceptedFile } from "@/utils/utils.js";
import { CanHandleDepartments } from '@/utils/permissions/permissions';

import Alert from '@/views/components/Alert/Alert';

const defaultValues = {
	title: '',
	filename: '',
	titleType: '',
	category: '',
	letterDate: null,
	author: {
		forename: '',
		surname: '',
	},
	recipient: '',
	department: { id: null },
};

const AddLetter = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const { recipients, titleTypes, categories } = useSelector(state => state.data);
	const { dict, mode } = useSelector((state) => state.app);
	const { user } = useSelector((state) => state.auth);

	const [ fileFromSimpleText, setFileFromSimpleText ] = useState();
	const [ fileFromSimpleTextId, setFileFromSimpleTextId ] = useState();

	const [ canSubmit, setCanSubmit ] = useState(false);

	const [ selectTitleTypes, setSelectTitleTypes ] = useState([]);
	const [ selectRecipients, setSelectRecipients ] = useState([]);
	const [ selectCategories, setSelectCategories ] = useState([]);

	const [ waitingForResponse, setWaitingForResponse ] = useState(false);

	const [ sensitiveDataWarning, setSensitiveDataWarning ] = useState(null);

	const [ dataToSend, setDataToSend ] = useState(null);

	// {type: "", children: <></>}
	const [ fileAlert, setFileAlert ] = useState();

	const getAcceptedFile = useAcceptedFile();
	const [ myFiles, setMyFiles ] = useState([]);

	// const { register, handleSubmit, setValue, reset, control, setError, getValues } = useForm({ defaultValues: defaultValues });
	const { register, handleSubmit, setValue, control, watch } = useForm({ defaultValues: defaultValues });

	// configure react-dropzone
	const {
		acceptedFiles,
		fileRejections,
		getRootProps,
		getInputProps,
	} = useDropzone({
		accept: {
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [ ".docx" ],
		},
		multiple: false,
		disabled: fileFromSimpleText,
	});

	const deptId = watch('department.id');
	const titleValue = watch('title');
	const titleTypeValue = watch('titleType');

	useEffect(() => {
		(async () => {
			const fileId = new URLSearchParams(location.search).get("fileId");

			if (fileId) {
				try {
					const downloadedFileContent = await api.get(`/ai/docx/${fileId}`, { ignoreRedirectInterceptors: true, responseType: 'blob' });

					const downloadedFile = new File([ downloadedFileContent.data ], `ai_created_${fileId}.docx`, {type:"application/vnd.openxmlformats-officedocument.wordprocessingml.document"});
					downloadedFile.path = `ai_created_${fileId}.docx`;

					setMyFiles(getAcceptedFile([ downloadedFile ]));
					setValue('filename', downloadedFile.path);

					setFileFromSimpleText(downloadedFile);
					setFileFromSimpleTextId(fileId);
					setFileAlert({ type: "success", children: <><strong>{dict.addLetter.simplifiedTextWithAI.part1}</strong> {dict.addLetter.simplifiedTextWithAI.part2}</>});
				} catch (error) {
					setFileAlert({ type: "error", children: <><strong>{dict.addLetter.simplifiedTextWithAIError.part1}</strong> {dict.addLetter.simplifiedTextWithAIError.part2}</>});
					// eslint-disable-next-line no-console
					console.log(error);
				}
			}
		})();
	}, []);

	useEffect(() => {
		if (myFiles.length < 1 || !deptId || deptId.length < 1) {
			setCanSubmit(false);
		} else {
			setCanSubmit(true);
		}
	}, [ myFiles, deptId ]);

	useEffect(() => {
		if(fileRejections.length > 0) {
			setFileAlert({type: "error", children: <><strong>{ dict.addLetter.errors.wrongFormat.part1 }</strong> { dict.addLetter.errors.wrongFormat.part2 }<br />{ dict.addLetter.errors.wrongFormat.part3 }</>});
		} else {
			setFileAlert(undefined);
		}
	}, [ fileRejections ]);

	const onSubmit = data => {
		setDataToSend(data);
	};

	useEffect(() => {
		if (acceptedFiles.length) {
			setMyFiles(getAcceptedFile(acceptedFiles));
			setValue('filename', acceptedFiles[0].name);
			setFileAlert({type: "info", children: <>{dict.addLetter.addedFile} <strong>{acceptedFiles[0].name}</strong></>});
		}
	}, [ acceptedFiles, setValue ]);

	const sendToApi = (skipValidation = false) => {
		if ((acceptedFiles.length === 1 || fileFromSimpleText) && dataToSend) {
			const formD = new FormData();

			if (fileFromSimpleText) {
				formD.append("docx", fileFromSimpleText);
			} else {
				formD.append("docx", acceptedFiles[0]);
			}

			let toSend = {
				title: dataToSend.title,
				date: (dataToSend.letterDate && dataToSend.letterDate.length > 1 ? dataToSend.letterDate : null),
				department: dataToSend.department,
				titleType: ((dataToSend.titleType.value === 'none' || dataToSend.titleType.value === 'incipit' || dataToSend.title.length < 1) ? null : dataToSend.titleType.value),
				author: dataToSend.author,
				recipient: dataToSend.recipient.value,
				category: dataToSend.category.value,
			};
			if (fileFromSimpleText) {
				toSend.version = "ai-created";
			}
			formD.append("json", JSON.stringify(toSend));
			if (skipValidation) {
				formD.append("validateEntities", "false");
			}

			setWaitingForResponse(true);
			api.post('/letters', formD, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}).then(({ data: { id } }) => {
				setWaitingForResponse(false);
				if (fileFromSimpleTextId) {
					api.delete(`/ai/docx/${fileFromSimpleTextId}`);
				}
				navigate(`/letter/${id}`);
			}).catch(({ response }) => {
				if (response.status === 400) {
					if (response.data.errors.length > 0) {
						if (response.data.errors[0].code === 'field.invalid'){
							return setSensitiveDataWarning(JSON.parse(response.data.errors[0].message));
						}
						const errCode = response.data.errors[0].code.replaceAll('.', '');
						setFileAlert({ type: "error", children: <>{dict.addLetter.errors[errCode]}</>});
					} else if (response?.data?.error) {
						setFileAlert({ type: "error", children: <>{response?.data?.error}</>});
					}
				}
				setWaitingForResponse(false);
			});
		}
	};
	const sendDataRegardlessTheWarnings = () => {
		if (dataToSend) {
			sendToApi(true);
		}
	};

	useEffect(() => {
		// send to api once data to send is set;
		if (dataToSend) {
			sendToApi();
		}
	}, [ dataToSend ]);

	useEffect(() => {
		if (!recipients) dispatch(dataActions.getRecipients());
		if (!titleTypes) dispatch(dataActions.getTitleTypes());
		if (!categories) dispatch(dataActions.getLetterCategories());

		if(user?.departments[0]?.id) setValue("department.id", user?.departments[0]?.id);

		// eslint-disable-next-line
    }, []);

	useEffect(() => {
		if (titleTypes) {
			const selectTitleTypesArray = [
				...titleTypes.map(item => ({
					value: item, label: dict.addLetter[item],
				})) ];
			setSelectTitleTypes(selectTitleTypesArray);
		}
	}, [ titleTypes ]);

	useEffect(() => {
		if (titleValue === "") {
			setValue("titleType", { value: 'incipit', label: dict.addLetter['incipit'] });
		} else {
			if (titleTypeValue?.value == 'incipit'){
				let grantedType = selectTitleTypes.find((type) => { return type.value === "granted"; });

				if (grantedType) {
					setValue("titleType", grantedType);
				} else {
					setValue("titleType", selectTitleTypes[0]);
				}
			}
		}
	}, [ selectTitleTypes, titleValue ]);

	useEffect(() => {
		if (titleValue === "") {
			setSelectTitleTypes([ { value: 'incipit', label: dict.addLetter['incipit'] } ]);
		} else {
			setSelectTitleTypes([ ...titleTypes.map(item => ({
				value: item, label: dict.addLetter[item],
			})) ]);
		}
	}, [ titleValue ]);

	useEffect(() => {
		if (recipients) {
			setSelectRecipients(recipients.map(item => ({
				value: item.code, label: item.label,
			})));
		}
		// eslint-disable-next-line
    }, [recipients]);

	useEffect(() => {
		if (categories) {
			setSelectCategories(categories.map(item => ({
				value: item.code, label: item.label,
			})));
		}
		// eslint-disable-next-line
    }, [categories]);

	useEffect(() => {
		if (selectRecipients.length) {
			setValue("recipient", selectRecipients[0]);
		}
		// eslint-disable-next-line
    }, [selectRecipients]);

	useEffect(() => {
		if (selectCategories.length) {
			setValue("category", selectCategories[0]);
		}
		// eslint-disable-next-line
    }, [selectCategories]);

	// for react-select
	const colorStyles = {
		control: styles => ({ ...styles, backgroundColor: '#FCFCFC', borderColor: '#DADADA', maxHeight: '32px', height: '32px', minHeight: '0' }),
		valueContainer: styles => ({ ...styles, padding: "0px 8px", maxHeight: '32px', height: '32px' }),
		indicatorsContainer: styles => ({ ...styles, maxHeight: '32px', height: '32px' }),
	};

	const cancelForm = (e) => {
		e.preventDefault();
		navigate(-1);
	};

	const closeSensitiveDataWarning = () => {
		setSensitiveDataWarning(null);
		setWaitingForResponse(false);
	};

	return (
		<div className='addletter__wrapper'>
			<AddLetterSensitiveDataWarningModal
				sensitiveDataWarning={ sensitiveDataWarning }
				closeSensitiveDataWarning={ closeSensitiveDataWarning }
				sendDataRegardlessTheWarnings={ sendDataRegardlessTheWarnings }
			/>
			<Header narrow />

			{waitingForResponse && (
				<div className='addletter__spinner'>
					<Spinner wrapper />
				</div>
			)}

			<main
				className='addletter__main'
			>
				<div className='addletter__main--container'>
					<div className='addletter__main--wrapper'>

						<h1 className='addletter__title'>
							{dict.addLetter.addLetter}
						</h1>

						<section className='addletter__comment--head'>
							<Alert
								variant='info'
								alertContent={ <><strong>{dict.addLetter.warn1}</strong> { dict.addLetter.warn2 }</> }
							/>
						</section>

						<section className='addletter__section--filedsRequiredInfo'>
							<div className='addletter__comment--grey'>{dict.addLetter.fieldsRequired}</div>
						</section>

						<form
							className='addletter__form'
							onSubmit={ handleSubmit(onSubmit) }
						>
							<section className='addletter__section--fileselect'>
								<label>{dict.addLetter.addLetter}<span style={ { color: '#5c5c5a' } }>{dict.addLetter.docx}</span></label>
								{fileAlert && (
									<Alert
										variant={ fileAlert.type }
										alertContent={ fileAlert.children }
									/>
								)}
								{fileAlert?.type === 'info' && (
									<label>To nie ten plik? Załaduj inny:</label>
								)}
								<div
									{ ...getRootProps({ className: `addletter__dropzone${ (fileAlert && fileAlert.type === 'error') ? ' error' : '' }` }) }
								>
									<input
										{ ...getInputProps() }
									/>
									<p><span>{dict.departments.modalUpload.browse}</span> {dict.departments.modalUpload.dragFiles}</p>
								</div>
							</section>
							<section className='addletter__section--title'>
								<label>{dict.addLetter.title}</label>
								<input
									{ ...register('title') }
									maxLength={ 4096 }
									placeholder={ dict.addLetter.titlePlaceholder }
								/>
								<div className='addletter__comment--grey'>
									<i className="icon-circle-info" /> {dict.addLetter.titleNote}
								</div>
							</section>

							<section>
								<label>{dict.addLetter.titleType}</label>
								<Controller
									name='titleType'
									render={ ({ field }) => <Select
										options={ selectTitleTypes }
										styles={ colorStyles }
										isSearchable={ false }
										classNamePrefix='letters__searchbar--filters-select'
										components={ {
											DropdownIndicator,
											NoOptionsMessage,
											IndicatorSeparator: () => null,
										} }
										isDisabled={ titleValue === '' }
										{ ...field }
									/> }
									control={ control }
								/>
								<div>
									<div className="addletter__title-type">
										<i className="icon-circle-info" />
										{dict.addLetter.titleTypes}
									</div>
									<ul className="addletter__title-type--list">
										<li>{dict.addLetter.titleTypesDescription.original}</li>
										<li>{dict.addLetter.titleTypesDescription.given}</li>
										<li>{dict.addLetter.titleTypesDescription.incipit}</li>
									</ul>
								</div>
							</section>
							<section className='addletter__section--date'>
								<label>{dict.addLetter.letterDate}</label>
								<AddLetterDatePicker
									setValue={ setValue }
								/>
								<input
									style={ { display: 'none' } }
									{ ...register('letterDate') }
								/>
							</section>
							<section className='addletter__section--author'>
								<label>{dict.addLetter.letterAuthor}</label>
								<div className='addletter__section--author-info'>{dict.addLetter.letterAuthorAdditionalInfo}</div>
								<div className='addletter__section--name-surname'>
									<div>
										<label>{ dict.addLetter.forename }</label>
										<input
											className='addletter__section--author-input'
											maxLength={ 256 }
											{ ...register('author.forename') }
										/>
									</div>
									<div>
										<label>{ dict.addLetter.surname }</label>
										<input
											className='addletter__section--author-input'
											maxLength={ 256 }
											{ ...register('author.surname') }
										/>
									</div>
								</div>
							</section>
							<section
								className='addletter__section--titletype'
							>
								<label>{dict.addLetter.category}</label>
								<Controller
									name='category'
									render={ ({ field }) => <Select
										options={ selectCategories }
										styles={ colorStyles }
										isSearchable={ false }
										classNamePrefix='letters__searchbar--filters-select'
										components={ {
											DropdownIndicator,
											NoOptionsMessage,
											IndicatorSeparator: () => null,
										} }
										{ ...field }
									/> }
									control={ control }
								/>
							</section>
							<section className='addletter__section--department'>
								<AddLetterDeptAutoSuggest
									label={ dict.addLetter.department }
									setValueExt={ setValue }
									defaultValue={ user?.departments?.length > 0 ?
										{
											id: user?.departments[0]?.id,
											label: user?.departments[0]?.name,
										} : null
									}
									disabled={ !CanHandleDepartments(user.role, mode) }
								/>
								<input
									style={ { display: 'none' } }
									{ ...register('department.id') }
								/>
							</section>
							<section className='addletter__section--recipient'>
								<label>{dict.addLetter.recipient}</label>
								<Controller
									name='recipient'
									render={ ({ field }) => <Select
										options={ selectRecipients }
										isSearchable={ false }
										styles={ colorStyles }
										classNamePrefix='letters__searchbar--filters-select'
										components={ {
											DropdownIndicator,
											NoOptionsMessage,
											IndicatorSeparator: () => null,
										} }
										{ ...field }
									/> }
									control={ control }
								/>
							</section>
							<section className='addletter__section--buttons'>
								<button
									className='button__std'
									onClick={ (e) => cancelForm(e) }
								>
									{dict.cancel}
								</button>
								<button
									className={ `button__blue${canSubmit ? '' : ' disabled'}` }
									type='submit'
								>
									{dict.addLetter.add}
								</button>
							</section>
						</form>
					</div>
				</div>
			</main>
			<Footer narrow={ false } />
		</div>
	);
};

export default AddLetter;
